import Vue from 'vue'
import config from 'config'
import getIquoteQuery from '../../graphql/b2c/getIQuote'
import getB2bIquoteQuery from '../../graphql/b2b/getIQuote'

import updateLocaleMutation from '../../graphql/b2c/updateLocale'
import confirmOrder from '../../graphql/common/confirmOrder'
import putActivityMutation from '../../graphql/b2c/putActivity'
import createPaymentMutation from '../../graphql/b2c/createPayment'
import { DateTime } from 'luxon'

const graphqlUrl = config.graphQl.url

export default {
  state: {
    iquoteId: '',
    iquoteLoadingError: '',
    iquote: '',
    iquoteErr: '',
    productsServices: [],
    quoteId: '',
  },
  getters: {
    configurationId: (state) => state.iquote && state.iquote.configurationId,
    languageId: (state) => state.iquote && state.iquote.languageId,
    theme: (state) => {
      if (
        state.iquote &&
        state.iquote.configurationId &&
        state.iquote.configurationId.includes('ENGIE')
      ) {
        return 'engieTheme'
      } else {
        return 'sungevityTheme'
      }
    },
    locale: (state) => {
      if (state.iquote) return state.iquote.locale
      else return guessLocale()
    },
    loadingError: (state) => state.iquoteLoadingError,
    loading: (state) => !state.iquoteLoadingError && !state.iquote,
    iquote: (state) => state.iquote,
    // systems are deprecated in new quotes
    quotes: (state) => state.iquote.quotes || state.iquote.systems,
    country: (state) => {
      const country = state.iquote && state.iquote.locale.split('_')[1].toLowerCase()
      // en_US is an option in Odoo but we don't support this country
      return country === 'us' ? 'nl' : country
    },
    iquoteId: (state) => state.iquoteId,
    quoteId: (state) => state.quoteId,
    iquoteErr: (state) => state.iquoteErr,
    quoteType: (state) => state.iquote.quoteType,
    isGeneric: (_state, getters) =>
      ['BATTERY', 'INVERTER', 'PANELS', 'METER', 'OTHER'].includes(getters.quoteType),
    isBattery: (_state, getters) => getters.quoteType === 'BATTERY',
    isIChoosr: (_state, getters) => getters.iquote.isIChooser,
    productsServices: (state) => state.productsServices,
    paymentStatus: (state) => state.paymentStatus,
    isFlanders: (_state, getters) => ['FLANDERS'].some((s) => getters.configurationId.includes(s)),
    isBrussels: (_state, getters) => ['BRUSSELS'].some((s) => getters.configurationId.includes(s)),
    isWallonia: (_state, getters) => ['WALLONIA'].some((s) => getters.configurationId.includes(s)),
    isFlandersOrBrussels: (_state, getters) => getters.isFlanders || getters.isBrussels,
    isBelgium: (_state, getters) => getters.country === 'be',
    isNetherlands: (_state, getters) => getters.country === 'nl',
    language: (_state, getters) => getters.locale.split('_')[0],
    offerExpiryDate: (state, getters) => {
      const offerExpiryDate = state.iquote.offerExpiryDate && DateTime.fromISO(state.iquote.offerExpiryDate)
      const language = getters.language
      return offerExpiryDate.setLocale(language).toLocaleString()
    },
    isExpired: (state) => {
      const { offerExpiryDate } = state.iquote

      return DateTime.now().startOf('day') > DateTime.fromISO(offerExpiryDate).startOf('day')
    },
    isAvailable: (state) => {
      const { viewableUntil } = state.iquote

      return DateTime.now().startOf('day') <= DateTime.fromISO(viewableUntil).startOf('day')
    },
    isOpenRedesign: ({ iquote }) => iquote.redesignState === 'redesign_not_approved',
    isCompletedRedesign: ({ iquote }) => iquote.redesignState === 'redesign_approved',

    // tabs logic
    showFinancingSection: (_state, getters) =>
      getters.isNetherlands && !getters.isIChoosr,
    showAddedLaborSection: (_state, getters) => getters.isIChoosr,
    company: (state) => state.iquote.company || {},
    contact: (state) => state.iquote.contact || {},
    fullName: (_state, getters) => `${getters.contact.firstName || ''} ${getters.contact.lastName}`.trim(),

    // TO DO: This is already a mixin. Either one can probably be removed
    chosenQuote: (state) => {
      if (!state.iquote || !state.iquote.chosenQuoteId) return
      return state.iquote.quotes.find(q => q.id === Number(state.iquote.chosenQuoteId)) 
    },
    chosenQuoteId: (state) => state.iquote && state.iquote.chosenQuoteId,
    streetAndHouseNumber: (state) => {
      const { address } = state.iquote.contact  
      return`${address.street} ${address.houseNumber} ${address.houseNumberAddition || ''}`
    },
  },
  mutations: {
    SET_IQUOTE_LOADING_ERROR(state, error) {
      state.iquoteLoadingError = error
    },
    // set the iquote id, we got from the routing param
    SET_IQUOTE_ID(state, iquoteId) {
      state.iquoteId = iquoteId
    },
    // set the quote id that is selected by the customer
    SET_QUOTE_ID(state, quoteId) {
      state.quoteId = quoteId
    },
    // set the entire iquote model
    SET_IQUOTE(state, iquote) {
      state.iquote = iquote
    },
    SET_IQUOTE_LOCALE(state, locale) {
      state.iquote.locale = locale
    },
    ADD_SERVICE_PRODUCTS_SERVICES(state, newProductServices) {
      // push new service to array
      state.productsServices.push(...newProductServices)
    },
    REMOVE_SERVICE_PRODUCTS_SERVICES(state, productsService) {
      // get array from state and filter. Return objects that are not equal to argument productsService (because that one will be removed)
      function removeService(service) {
        return service.productId !== productsService.productId
      }
      // remove selected service by filtering
      const existingServices = state.productsServices.filter(removeService)
      state.productsServices = existingServices
    },
    TOGGLE_SERVICE_PRODUCTS_SERVICES(state, { service, selected }) {
      if (selected) state.productsServices.push(service)
      if (!selected)
        state.productsServices = state.productsServices.filter(
          (s) => s.productId !== service.productId
        )
    },
    RESET_SERVICE_PRODUCTS_SERVICES(state) {
      state.productsServices = []
    },
  },
  actions: {
    // set the iquote id from the routing url
    setIquoteId(context, iquoteId) {
      context.commit('SET_IQUOTE_ID', iquoteId)
    },
    // get the entire iquote (on boostrap vue)
    getIquote(context, type = 'b2c') {
      const query = type === 'b2c' ? getIquoteQuery(context.getters.iquoteId) : getB2bIquoteQuery(context.getters.iquoteId)
      const body = { query }
      const url = graphqlUrl

      return Vue.$http
        .post(url, body, { headers: { 'API-KEY': config.graphQl.key } })
        .then((response) => {
          const data = response.data.data.getIQuote

          if (data === null) {
            context.commit('SET_IQUOTE_LOADING_ERROR', 'NOT_FOUND')
          } else if (data.error) {
            context.commit('SET_IQUOTE_LOADING_ERROR', 'ERROR')

            // This isInvalidIQuote check should probably be removed 
            // when there is some validation in the backend
          } else if (isInvalidIQuote(data, type)) {
            context.commit('SET_IQUOTE_LOADING_ERROR', 'DATA_ERROR')
          } else {
            context.commit('SET_IQUOTE', data)
          }
          return data
        })
        .catch((e) => {
          context.commit('SET_IQUOTE_LOADING_ERROR', 'ERROR')
        })
    },
    async confirmOrder(_context, payload) { 
      const body = confirmOrder(payload)
      try {
        const response = await Vue.$http.post(graphqlUrl, body, { headers: { 'api-key': config.graphQl.key }})
        if (response.data.errors || response.data.data.confirmSaleOrder.result !== 'success' ) throw new Error('Error confirming order')

        return 'OK'
      }
      catch(e) {
        return 'ERROR'
      }
    },
    postPayment(context, body) {
      const query = createPaymentMutation(
        body.chosenQuoteId,
        body.chosenAddons
      )
      return Vue.$http.post(graphqlUrl, query, { headers: { 'api-key': config.graphQl.key } })
    },
    // switch user locale on iquote
    setIquoteLocale(context, locale) {
      context.commit('SET_IQUOTE_LOCALE', locale)
      const query = updateLocaleMutation(context.getters.iquoteId, locale)
      const body = { query }
      return Vue.$http.post(graphqlUrl, body, { headers: { 'api-key': config.graphQl.key } })
    },
    putActivity(context, activity) {
      // get external preview mode from context
      const externalQuery = context.getters.externalQuery
      if (externalQuery && externalQuery.preview) {
        // don't do put call when preview mode === true
        return null
      }
      const query = putActivityMutation(
        activity.quoteId,
        activity.activity,
      )
      const body = { query }
      return Vue.$http.post(graphqlUrl, body, { headers: { 'API-KEY': config.graphQl.key } })
    },
  },
}

function guessLocale() {
  const hostMatch = /sungevity.(nl|be|it)/g.exec(window.location.hostname)
  const country = hostMatch ? hostMatch[1] : 'be'

  const browserLanguage = navigator.languages
    ? navigator.languages[0]
    : navigator.language || navigator.browserLanguage
  const browserLocale = browserLanguage.replace(/-/gi, '_')
  const locales = config['available.locales'][country]

  if (locales.length === 1) return locales[0]
  if (locales.includes(browserLocale)) return browserLocale

  return locales[0]
}

function isInvalidIQuote(data, type) {
  const availableLocales = ['nl_NL', 'fr_BE', 'nl_BE']
  /* eslint-disable */
  if (!data.viewableUntil) {
    console.log('No viewableUntil found')
    return true
  }
  if (!availableLocales.includes(data.locale)) {
    console.log('Locale is not valid: ', data.locale)
    return true
  }
  if (!data.quotes || data.quotes.length === 0) {
    console.log('No quotes found')
    return true
  }
  if (type !== 'b2b' && !data.offerExpiryDate) {
    console.log('No offerExpiryDate found')
    return true
  }
  if (type === 'b2b' && !data.company) {
    console.log('No company found')
    return true
  }
  if (type === 'b2b' && !data.company.name) {
    console.log('No company name found')
    return true
  }
  if (!data.quotes[0].termsAndConditions.length > 0) {
    console.log('No termsAndConditions set for a quote')
    return true
  }
  if (data.quoteType === 'PV' && !data.quotes[0].items.some(item => item.category === 'Inverters')) {
    console.log('No inverter found in one of the quotes')
    return true
  }
  if (type != 'b2b' && inCorrectLocaleAndConfigurationId(data)) {
    console.log('Locale and configuration do not match')
    return true
  }

  if (type === 'b2b' && !data.contact.address || !data.contact.address.street || !data.contact.address.houseNumber) {
    console.log('Customer address not complete')
    return true
  }

  return false
}


function inCorrectLocaleAndConfigurationId(iquote) {
  if (iquote.locale === 'nl_NL' && iquote.configurationId != 'SUNGEVITY_NL') return true
  else if ((iquote.locale === 'nl_BE' || iquote.locale === 'fr_BE') && !iquote.configurationId.startsWith('ENGIE')) return true
  else return false
}
